import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, Building2, Users, LogOut, Settings, Lock, Trash2,
  CheckCircle, XCircle, Edit, Search, Filter, ChevronDown, ChevronUp,
  Plus, Eye, Pause, Play, Copy, Mail, Calendar, DollarSign, Globe,
  AlertCircle, TrendingUp, Clock, X, Check, FileText, Package, Info
} from 'lucide-react';
import TenantModuleManagement from '../../components/TenantModuleManagement';
import { useAlert } from '../../context/AlertContext';

const PlatformAdminDashboard = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();
  const [admin, setAdmin] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [pagination, setPagination] = useState({ skip: 0, limit: 25, total: 0 });

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  useEffect(() => {
    // Check if admin is logged in
    const adminData = localStorage.getItem('platform_admin');
    const token = localStorage.getItem('platform_admin_token');

    if (!adminData || !token) {
      navigate('/saarv-admin/login');
      return;
    }

    try {
      setAdmin(JSON.parse(adminData));
      fetchTenants();
      fetchModules();
    } catch (e) {
      console.error('Error parsing admin data:', e);
      navigate('/saarv-admin/login');
    }
  }, [navigate, pagination.skip, pagination.limit, searchTerm, statusFilter, planFilter]);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('platform_admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const params = {
        skip: pagination.skip,
        limit: pagination.limit
      };

      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (planFilter !== 'all') params.plan = planFilter;

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/tenants`,
        { headers, params }
      );

      setTenants(response.data.tenants || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Error fetching tenants:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const token = localStorage.getItem('platform_admin_token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/available-modules`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableModules(response.data.module_details || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('platform_admin_token');
    localStorage.removeItem('platform_admin');
    navigate('/saarv-admin/login');
  };

  const handleSuspendTenant = async (tenantId, reason = "Administrative action") => {
    try {
      const token = localStorage.getItem('platform_admin_token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/tenants/${tenantId}/suspend`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Tenant suspended successfully');
      fetchTenants();
    } catch (error) {
      showError('Error suspending tenant: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleReactivateTenant = async (tenantId) => {
    try {
      const token = localStorage.getItem('platform_admin_token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/tenants/${tenantId}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Tenant reactivated successfully');
      fetchTenants();
    } catch (error) {
      showError('Error reactivating tenant: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSoftDeleteTenant = async (tenantId, tenantName) => {
    const confirmed = await confirm(
      'Delete Tenant',
      `Are you sure you want to delete ${tenantName}? This is a soft delete with a 30-day recovery period.`,
      { destructive: true, confirmLabel: 'Delete' }
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('platform_admin_token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/tenants/${tenantId}/soft`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Tenant soft deleted successfully (30-day recovery period)');
      fetchTenants();
    } catch (error) {
      showError('Error deleting tenant: ' + (error.response?.data?.detail || error.message));
    }
  };

  const openDetailModal = async (tenant) => {
    try {
      const token = localStorage.getItem('platform_admin_token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/tenants/${tenant.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedTenant(response.data);
      setShowDetailModal(true);
    } catch (error) {
      showError('Error fetching tenant details: ' + (error.response?.data?.detail || error.message));
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading platform data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/saarv-logo.svg" alt="Saarv Logo" className="h-10 w-auto" />
              <div className="border-l-2 border-gray-300 pl-3">
                <h1 className="text-2xl font-bold text-gray-900">Platform Admin</h1>
                <p className="text-sm text-gray-600">Tenant Management Portal</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{admin?.username}</p>
                <p className="text-xs text-gray-600">{admin?.email}</p>
              </div>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Change Password"
              >
                <Lock className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Tenants</p>
                <p className="text-3xl font-bold text-gray-900">{pagination.total}</p>
              </div>
              <Building2 className="w-12 h-12 text-blue-600 opacity-20" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Active Tenants</p>
                <p className="text-3xl font-bold text-gray-900">
                  {tenants.filter(t => t.is_active && !t.soft_deleted).length}
                </p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-600 opacity-20" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Users</p>
                <p className="text-3xl font-bold text-gray-900">
                  {tenants.reduce((sum, t) => sum + (t.user_count || 0), 0)}
                </p>
              </div>
              <Users className="w-12 h-12 text-purple-600 opacity-20" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Suspended</p>
                <p className="text-3xl font-bold text-gray-900">
                  {tenants.filter(t => !t.is_active && !t.soft_deleted).length}
                </p>
              </div>
              <Pause className="w-12 h-12 text-yellow-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or domain..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination(prev => ({ ...prev, skip: 0 }));
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-3">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination(prev => ({ ...prev, skip: 0 }));
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="deleted">Deleted</option>
              </select>

              <select
                value={planFilter}
                onChange={(e) => {
                  setPlanFilter(e.target.value);
                  setPagination(prev => ({ ...prev, skip: 0 }));
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Plans</option>
                <option value="free">Free</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>

              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-colors shadow-sm"
              >
                <Plus className="w-5 h-5" />
                <span className="font-semibold">Create Tenant</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tenants List */}
        <div className="space-y-4 mb-6">
          {tenants.map((tenant) => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              availableModules={availableModules}
              onView={() => openDetailModal(tenant)}
              onSuspend={() => handleSuspendTenant(tenant.id)}
              onReactivate={() => handleReactivateTenant(tenant.id)}
              onDelete={() => handleSoftDeleteTenant(tenant.id, tenant.company_name)}
            />
          ))}

          {tenants.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No tenants found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || statusFilter !== 'all' || planFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Create your first tenant to get started'}
              </p>
              {!searchTerm && statusFilter === 'all' && planFilter === 'all' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-5 h-5" />
                  <span>Create First Tenant</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} tenants
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <TenantCreateModal
          availableModules={availableModules}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchTenants();
          }}
        />
      )}

      {showDetailModal && selectedTenant && (
        <TenantDetailModal
          tenant={selectedTenant}
          availableModules={availableModules}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTenant(null);
          }}
          onEdit={() => {
            setShowDetailModal(false);
            setShowEditModal(true);
          }}
          onRefresh={fetchTenants}
        />
      )}

      {showEditModal && selectedTenant && (
        <TenantEditModal
          tenant={selectedTenant}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTenant(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedTenant(null);
            fetchTenants();
          }}
        />
      )}

      {showPasswordModal && (
        <PasswordChangeModal onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
};

// Tenant Card Component
const TenantCard = ({ tenant, availableModules, onView, onSuspend, onReactivate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  const statusBadge = () => {
    if (tenant.soft_deleted) {
      return <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">Deleted</span>;
    }
    if (tenant.is_active) {
      return <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">Active</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">Suspended</span>;
  };

  const planBadge = (plan) => {
    const colors = {
      free: 'bg-gray-100 text-gray-800',
      starter: 'bg-blue-100 text-blue-800',
      professional: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-indigo-100 text-indigo-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${colors[plan] || colors.free}`}>
        {(plan || 'free').charAt(0).toUpperCase() + (plan || 'free').slice(1)}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-xl font-bold text-gray-900">{tenant.company_name}</h3>
              {statusBadge()}
              {planBadge(tenant.subscription_plan)}
            </div>
            <p className="text-sm text-gray-600 mb-1">{tenant.company_email}</p>
            {tenant.industry && (
              <p className="text-xs text-gray-500 mb-3">{tenant.industry}</p>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-4 text-sm mt-4">
              <div>
                <p className="text-gray-600">Users</p>
                <p className="font-semibold text-gray-900">{tenant.user_count || 0}</p>
              </div>
              <div>
                <p className="text-gray-600">Modules</p>
                <p className="font-semibold text-gray-900">{tenant.enabled_modules?.length || 0}/9</p>
              </div>
              <div>
                <p className="text-gray-600">Created</p>
                <p className="font-semibold text-gray-900">
                  {new Date(tenant.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Plan</p>
                <p className="font-semibold text-gray-900 capitalize">{tenant.subscription_plan || 'free'}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-2 ml-4">
            <button
              onClick={onView}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Eye className="w-4 h-4" />
              <span>View Details</span>
            </button>

            {!tenant.soft_deleted && (
              <>
                {tenant.is_active ? (
                  <button
                    onClick={onSuspend}
                    className="flex items-center space-x-2 px-3 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors text-sm"
                  >
                    <Pause className="w-4 h-4" />
                    <span>Suspend</span>
                  </button>
                ) : (
                  <button
                    onClick={onReactivate}
                    className="flex items-center space-x-2 px-3 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors text-sm"
                  >
                    <Play className="w-4 h-4" />
                    <span>Reactivate</span>
                  </button>
                )}

                <button
                  onClick={onDelete}
                  className="flex items-center space-x-2 px-3 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Expandable Modules */}
        {tenant.enabled_modules && tenant.enabled_modules.length > 0 && (
          <>
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-4 flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <span>{expanded ? 'Hide' : 'Show'} Enabled Modules</span>
            </button>

            {expanded && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {tenant.enabled_modules.map(moduleId => {
                    const module = availableModules.find(m => m.id === moduleId);
                    return (
                      <span key={moduleId} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {module?.name || moduleId}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Tenant Create Modal Component
const TenantCreateModal = ({ availableModules, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    admin_name: '',
    admin_email: '',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    billing_contact_name: '',
    billing_contact_email: '',
    billing_contact_phone: '',
    industry: '',
    custom_domain: '',
    subscription_plan: 'free',
    enabled_modules: [],
    timezone: 'UTC',
    locale: 'en-US',
    date_format: 'YYYY-MM-DD',
    logo_url: '',
    primary_color: '#3b82f6'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(true);

  // Helper function to format error messages
  const formatErrorMessage = (err) => {
    // Handle different error response formats
    const detail = err.response?.data?.detail;

    // If detail is a string, return it
    if (typeof detail === 'string') {
      return detail;
    }

    // If detail is an object with a message property
    if (detail && typeof detail === 'object' && detail.message) {
      return detail.message;
    }

    // If detail is an array (Pydantic validation errors)
    if (Array.isArray(detail)) {
      return detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
    }

    // If detail is an object (single Pydantic error or structured error)
    if (detail && typeof detail === 'object') {
      // Try to extract error message from common formats
      if (detail.error) return detail.error;
      if (detail.msg) return detail.msg;
      // If it's a validation error object, format it
      return JSON.stringify(detail);
    }

    // Fallback to generic message
    return err.response?.data?.message || err.message || 'Failed to create tenant';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('platform_admin_token');

      // Clean up form data - remove empty strings for optional fields
      const cleanedData = { ...formData };

      // List of optional fields that should be null instead of empty string
      const optionalFields = [
        'primary_contact_name',
        'primary_contact_email',
        'primary_contact_phone',
        'billing_contact_name',
        'billing_contact_email',
        'billing_contact_phone',
        'industry',
        'custom_domain',
        'logo_url'
      ];

      // Convert empty strings to null for optional fields
      optionalFields.forEach(field => {
        if (cleanedData[field] === '') {
          cleanedData[field] = null;
        }
      });

      // Remove null values entirely (backend will use defaults)
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === null || cleanedData[key] === '') {
          delete cleanedData[key];
        }
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/tenants`,
        cleanedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
    } catch (err) {
      console.error('Tenant creation error:', err.response?.data);
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(result.temporary_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (result) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full my-8">
          <div className="p-6 border-b border-gray-200 bg-green-50">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Tenant Created Successfully!</h2>
                <p className="text-sm text-gray-600 mt-1">{result.tenant_name}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {/* Prominent warning banner */}
            {!result.welcome_email_sent && (
              <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-yellow-900 mb-1">⚠️ Email Not Sent - Copy Credentials Now!</h3>
                    <p className="text-sm text-yellow-800">
                      SMTP is not configured. The welcome email was not sent. You MUST copy the temporary password below - it will not be shown again.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <h3 className="font-semibold text-blue-900 mb-2">Important: Save These Credentials</h3>
              <p className="text-sm text-blue-800">
                The temporary password will not be shown again. Make sure to copy it now.
              </p>
            </div>

            {/* Prominent Password Display */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300 p-6 rounded-lg">
              <label className="block text-lg font-bold text-purple-900 mb-3">🔑 Temporary Password</label>
              <div className="space-y-3">
                <div className="bg-white p-4 rounded-lg border-2 border-purple-200">
                  <p className="text-2xl font-mono font-bold text-gray-900 break-all text-center tracking-wide">
                    {showPassword ? result.temporary_password : '••••••••••••••••••••••••'}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center justify-center space-x-2"
                  >
                    {showPassword ? (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                        <span>Hide Password</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span>Show Password</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={copyPassword}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 font-semibold"
                  >
                    {copied ? (
                      <>
                        <Check className="w-5 h-5" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5" />
                        <span>Copy Password</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Tenant ID</label>
                <p className="text-sm text-gray-900 font-mono bg-white p-2 rounded border">{result.tenant_id}</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Admin Email</label>
                <p className="text-sm text-gray-900 font-mono bg-white p-2 rounded border">{result.admin_email}</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Subscription Plan</label>
                <p className="text-sm text-gray-900 capitalize">{result.subscription_plan}</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Enabled Modules</label>
                {result.enabled_modules && result.enabled_modules.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {result.enabled_modules.map(mod => (
                      <div key={mod} className="flex items-center space-x-2 p-2 bg-green-50 border border-green-200 rounded">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-gray-900 capitalize">{mod.replace(/-/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No modules enabled (Free plan default)</p>
                )}
              </div>

              {result.welcome_email_sent && (
                <div className="flex items-center space-x-2 p-3 rounded bg-green-100 text-green-800">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">✓ Welcome email sent successfully to {result.admin_email}</span>
                </div>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 p-4 rounded">
              <p className="text-sm text-amber-800">
                <strong>⚠️ Security Note:</strong> The admin must change this password on first login to comply with SOC 2 requirements.
              </p>
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 flex justify-end">
            <button
              onClick={onSuccess}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full my-8">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Create New Tenant</h2>
              <p className="text-sm text-gray-600 mt-1">SOC 2 Controlled Provisioning</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[70vh]">
          <div className="space-y-6">
            {/* Company Information */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Acme Corporation"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Industry</label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Manufacturing"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Custom Domain</label>
                  <input
                    type="text"
                    value={formData.custom_domain}
                    onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="acme.saarvsafety.com"
                  />
                </div>
              </div>
            </div>

            {/* Initial Admin User */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Initial Admin User</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Admin Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.admin_name}
                    onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Admin Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.admin_email}
                    onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="john@acme.com"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Name</label>
                  <input
                    type="text"
                    value={formData.primary_contact_name}
                    onChange={(e) => setFormData({ ...formData, primary_contact_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Email</label>
                  <input
                    type="email"
                    value={formData.primary_contact_email}
                    onChange={(e) => setFormData({ ...formData, primary_contact_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Phone</label>
                  <input
                    type="tel"
                    value={formData.primary_contact_phone}
                    onChange={(e) => setFormData({ ...formData, primary_contact_phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Subscription & Modules */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <span>Subscription Plan</span>
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Plan <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.subscription_plan}
                  onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="free">Free (5 users, 1GB) - Core only</option>
                  <option value="starter">Starter (25 users, 10GB) - HIRA, PTW, CAPA</option>
                  <option value="professional">Professional (100 users, 50GB) - All Safety + Energy</option>
                  <option value="enterprise">Enterprise (Unlimited) - All modules</option>
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <Info className="w-3 h-3 inline mr-1" />
                  Modules will be auto-enabled based on the plan. Customize module access after creation via "Edit" → "Module Management" tab.
                </p>
              </div>
            </div>

            {/* Settings */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Timezone</label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Locale</label>
                  <select
                    value={formData.locale}
                    onChange={(e) => setFormData({ ...formData, locale: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="en-US">English (US)</option>
                    <option value="en-GB">English (UK)</option>
                    <option value="es-ES">Spanish</option>
                    <option value="fr-FR">French</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date Format</label>
                  <select
                    value={formData.date_format}
                    onChange={(e) => setFormData({ ...formData, date_format: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                {error}
              </div>
            )}
          </div>
        </form>

        <div className="p-6 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            A secure temporary password will be generated and sent via email
          </p>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Create Tenant</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Continue with TenantDetailModal, TenantEditModal, and PasswordChangeModal in next message due to length...
// (I'll create these in a separate file to continue)

// Password Change Modal (keeping existing)
const PasswordChangeModal = ({ onClose }) => {
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (passwords.new_password !== passwords.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    if (passwords.new_password.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('platform_admin_token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/change-password`,
        {
          current_password: passwords.current_password,
          new_password: passwords.new_password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Change Password</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Current Password</label>
            <input
              type="password"
              value={passwords.current_password}
              onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
            <input
              type="password"
              value={passwords.new_password}
              onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm New Password</label>
            <input
              type="password"
              value={passwords.confirm_password}
              onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{error}</div>}
          {success && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">Password changed successfully!</div>}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Tenant Detail Modal - simplified placeholder for now
const TenantDetailModal = ({ tenant, availableModules, onClose, onEdit, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full my-8">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{tenant.company_name}</h2>
              <p className="text-sm text-gray-600 mt-1">Tenant Management</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === 'overview'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('modules')}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === 'modules'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Modules
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {activeTab === 'overview' && (
            <div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Email</h3>
              <p className="text-gray-900">{tenant.company_email}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Status</h3>
              <p className={`inline-flex px-3 py-1 rounded text-sm font-semibold ${
                tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {tenant.is_active ? 'Active' : 'Suspended'}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Plan</h3>
              <p className="text-gray-900 capitalize">{tenant.subscription_plan || 'free'}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Created</h3>
              <p className="text-gray-900">{new Date(tenant.created_at).toLocaleString()}</p>
            </div>
          </div>

          {tenant.enabled_modules && tenant.enabled_modules.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-700 mb-3">Enabled Modules</h3>
              <div className="flex flex-wrap gap-2">
                {tenant.enabled_modules.map(moduleId => {
                  const module = availableModules.find(m => m.id === moduleId);
                  return (
                    <span key={moduleId} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {module?.name || moduleId}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {tenant.usage_stats && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-700 mb-3">Usage Statistics</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Users</p>
                  <p className="text-2xl font-bold text-gray-900">{tenant.usage_stats.users || 0}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">HIRA</p>
                  <p className="text-2xl font-bold text-gray-900">{tenant.usage_stats.hira_assessments || 0}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Permits</p>
                  <p className="text-2xl font-bold text-gray-900">{tenant.usage_stats.permits || 0}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">CAPAs</p>
                  <p className="text-2xl font-bold text-gray-900">{tenant.usage_stats.capas || 0}</p>
                </div>
              </div>
            </div>
          )}

          {tenant.recent_audit_logs && tenant.recent_audit_logs.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-700 mb-3">Recent Audit Log (Last 10 Events)</h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                {tenant.recent_audit_logs.map((log, index) => (
                  <div key={index} className="text-xs text-gray-700 py-2 border-b border-gray-200 last:border-0">
                    <span className="font-semibold">{new Date(log.timestamp).toLocaleString()}</span> -
                    <span className="ml-1">{log.action}</span> by
                    <span className="ml-1 font-semibold">{log.actor.email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
            </div>
          )}

          {/* Modules Tab */}
          {activeTab === 'modules' && (
            <TenantModuleManagement
              tenantId={tenant.id}
              tenantName={tenant.company_name}
              onUpdate={onRefresh}
            />
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
            Close
          </button>
          <button onClick={onEdit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Edit Tenant
          </button>
        </div>
      </div>
    </div>
  );
};

// Tenant Edit Modal - with tabs like Detail modal
const TenantEditModal = ({ tenant, onClose, onSuccess }) => {
  const { showSuccess } = useAlert();
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    company_name: tenant.company_name || '',
    industry: tenant.industry || '',
    subscription_plan: tenant.subscription_plan || 'free'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatErrorMessage = (err) => {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && detail.message) return detail.message;
    if (Array.isArray(detail)) {
      return detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
    }
    if (detail && typeof detail === 'object') {
      if (detail.error) return detail.error;
      if (detail.msg) return detail.msg;
      return JSON.stringify(detail);
    }
    return err.response?.data?.message || err.message || 'Failed to update tenant';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('platform_admin_token');
      const cleanedData = { ...formData };

      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '') cleanedData[key] = null;
      });
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === null) delete cleanedData[key];
      });

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/tenants/${tenant.id}`,
        cleanedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Tenant updated successfully');
      onSuccess();
    } catch (err) {
      console.error('Tenant update error:', err.response?.data);
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Edit Tenant</h2>
              <p className="text-sm text-gray-600 mt-1">{tenant.company_name}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('basic')}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === 'basic'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Basic Information
            </button>
            <button
              onClick={() => setActiveTab('modules')}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === 'modules'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Module Management
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Company Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Industry</label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Manufacturing, Healthcare"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Subscription Plan <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.subscription_plan}
                    onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="free">Free (5 users, 1GB)</option>
                    <option value="starter">Starter (25 users, 10GB)</option>
                    <option value="professional">Professional (100 users, 50GB)</option>
                    <option value="enterprise">Enterprise (Unlimited)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Current plan: <span className="font-semibold capitalize">{tenant.subscription_plan}</span>
                  </p>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Note about Module Management</p>
                    <p className="text-xs text-blue-700 mt-1">
                      To manage individual modules for this tenant, switch to the "Enabled Modules" tab. Changes to the subscription plan may affect available modules.
                    </p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Enabled Modules Tab - Full Module Management */}
          {activeTab === 'modules' && (
            <TenantModuleManagement
              tenantId={tenant.id}
              tenantName={tenant.company_name}
              onUpdate={onSuccess}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {activeTab === 'basic' ? 'Update basic tenant information' : 'Manage module access and subscriptions'}
          </p>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              {activeTab === 'modules' ? 'Close' : 'Cancel'}
            </button>
            {activeTab === 'basic' && (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminDashboard;
